// Shot Map widget (Players canvas) — a custom SVG pitch with a dot per shot the
// player took in the selected competition + season. No `soccer-vis` (§7B
// decision 1): the pitch and the dots are hand-drawn SVG.
//
// The maths (coordinate mapping, outcome classification, xG→radius, the summary)
// all live in the pure, unit-tested `shotMap.ts`; this file is just the view.
// Consumes player + competition + season (same scope FE5's Stats widget uses).

import type { PlayerShot } from "@catbento/shared";
import { usePlayerShots } from "../data/players.js";
import type { WidgetScope } from "./scope.js";
import { theme } from "../lib/theme.js";
import {
  VIEW,
  OUTCOME_COLOR,
  OUTCOME_LABEL,
  classifyShot,
  outcomeName,
  shotXg,
  pitchToSvg,
  dotRadius,
  summarizeShots,
  type ShotOutcome,
} from "./shotMap.js";

// Faint line colour for the pitch markings (muted ink at low alpha).
const LINE = "rgba(148,163,184,0.35)";

export default function ShotMapWidget({ scope }: { scope: WidgetScope }) {
  const { playerId, competitionId, seasonId } = scope;
  const { data, isPending, isError, error } = usePlayerShots(playerId, competitionId, seasonId);

  if (playerId === null) return <Msg>Search for a player above.</Msg>;
  if (competitionId === null || seasonId === null)
    return <Msg>Pick a competition and season above.</Msg>;
  if (isPending) return <Msg>Loading shots…</Msg>;
  if (isError) return <Msg danger>Couldn't load shots. {String(error)}</Msg>;

  const shots = data.shots;
  if (shots.length === 0) return <Msg>No shots for this player in this season.</Msg>;

  const summary = summarizeShots(shots);
  // Only shots with coordinates can be plotted; count any dropped so the header
  // is honest rather than silently under-plotting (the #49 lesson).
  const plottable = shots.filter((s) => s.x !== null && s.y !== null);
  const dropped = shots.length - plottable.length;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 8 }}>
      <div style={{ color: theme.inkMuted, fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
        <span style={{ color: theme.ink, fontWeight: 600 }}>{summary.total}</span> shots ·{" "}
        <span style={{ color: OUTCOME_COLOR.goal, fontWeight: 600 }}>{summary.goals}</span> goals ·{" "}
        {summary.totalXg.toFixed(1)} xG
        {dropped > 0 && <span style={{ color: theme.inkFaint }}> · {dropped} without location</span>}
      </div>

      <div style={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center" }}>
        <svg
          viewBox={`-3 -4 ${VIEW.width + 6} ${VIEW.height + 7}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ width: "100%", height: "100%", maxHeight: "100%" }}
          role="img"
          aria-label={`Shot map: ${summary.total} shots, ${summary.goals} goals`}
        >
          <Pitch />
          {plottable.map((shot) => (
            <ShotDot key={shot.id} shot={shot} />
          ))}
        </svg>
      </div>

      <Legend goals={summary.goals} onTarget={summary.onTarget} offTarget={summary.offTarget} />
    </div>
  );
}

/** The attacking third, goal at the top — drawn in viewBox/pitch units so the
 *  markings line up with the mapped shot coordinates. Geometry is StatsBomb:
 *  penalty box x 102–120 / y 18–62, 6-yard box x 114–120 / y 30–50, penalty
 *  spot at (108, 40) — all pre-mapped to the sy = 120 − x, sx = y frame. */
function Pitch() {
  return (
    <g fill="none" stroke={LINE} strokeWidth={0.4}>
      {/* Field area (also paints the pitch background). */}
      <rect x={0} y={0} width={VIEW.width} height={VIEW.height} fill={theme.tile} />
      {/* Penalty box (x 102–120 → sy 0–18; y 18–62 → sx 18–62). */}
      <rect x={18} y={0} width={44} height={18} />
      {/* 6-yard box (x 114–120 → sy 0–6; y 30–50 → sx 30–50). */}
      <rect x={30} y={0} width={20} height={6} />
      {/* Penalty arc — the part of the 10-unit circle round the spot outside the box. */}
      <path d="M 32 18 A 10 10 0 0 1 48 18" />
      {/* Penalty spot (108, 40) → (sx 40, sy 12). */}
      <circle cx={40} cy={12} r={0.5} fill={LINE} stroke="none" />
      {/* Goal mouth (x=120, y 36–44) → a bar just above the top goal line. */}
      <rect x={36} y={-1.4} width={8} height={1.4} fill={theme.inkFaint} stroke="none" />
    </g>
  );
}

function ShotDot({ shot }: { shot: PlayerShot }) {
  // x/y are non-null here (filtered by the caller), but TS doesn't know that.
  const { sx, sy } = pitchToSvg(shot.x as number, shot.y as number);
  const outcome: ShotOutcome = classifyShot(shot);
  const xg = shotXg(shot);
  const r = dotRadius(xg);
  const isGoal = outcome === "goal";

  return (
    <circle
      cx={sx}
      cy={sy}
      r={r}
      fill={OUTCOME_COLOR[outcome]}
      fillOpacity={0.85}
      stroke={isGoal ? "#ffffff" : "rgba(255,255,255,0.5)"}
      strokeWidth={isGoal ? 0.5 : 0.25}
    >
      <title>{shotTitle(shot, outcome, xg)}</title>
    </circle>
  );
}

/** Native hover tooltip text: minute, opponent, outcome, xG. */
function shotTitle(shot: PlayerShot, outcome: ShotOutcome, xg: number | null): string {
  const parts: string[] = [];
  if (shot.minute !== null) parts.push(`${shot.minute}'`);
  if (shot.oppositionName) parts.push(`vs ${shot.oppositionName}`);
  parts.push(outcomeName(shot) ?? OUTCOME_LABEL[outcome]);
  if (xg !== null) parts.push(`${xg.toFixed(2)} xG`);
  return parts.join(" · ");
}

function Legend({
  goals,
  onTarget,
  offTarget,
}: {
  goals: number;
  onTarget: number;
  offTarget: number;
}) {
  const items: { outcome: ShotOutcome; count: number }[] = [
    { outcome: "goal", count: goals },
    { outcome: "on_target", count: onTarget },
    { outcome: "off_target", count: offTarget },
  ];
  return (
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: theme.inkMuted }}>
      {items.map(({ outcome, count }) => (
        <span key={outcome} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span
            style={{
              width: 9,
              height: 9,
              borderRadius: "50%",
              background: OUTCOME_COLOR[outcome],
              display: "inline-block",
            }}
          />
          {OUTCOME_LABEL[outcome]} <span style={{ color: theme.inkFaint }}>{count}</span>
        </span>
      ))}
    </div>
  );
}

function Msg({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div style={{ color: danger ? theme.danger : theme.inkMuted, fontSize: 13 }}>{children}</div>;
}
